import React, { useState, useEffect } from "react";
import {
  GetAllFollowUpTypes,
  GetUserTasksByLeadId,
  GetLeadLog,
} from "../Services/APIService";
import { useNavigate } from "react-router-dom";
import UpdatedUserTaskComponent from "./UpdatedUserTaskComponent";

// Interfaces
interface followupTypeList {
  id: number;
  name: string;
  isActive: boolean;
}

export interface UserTaskInterface {
  id: number;
  leadId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  isPostponed: boolean;
  postponeReason: string | null;
  rescheduleDate: string | null;
  reminderDate: string;
  isCompleted: boolean;
  isCanceled: boolean;
  taskCompletionDate: string | null;
  createdBy: number;
  createdOn: string;
  modifiedBy: number | null;
  modifiedOn: string;
  projectName: string;
  assignedUserName: string;
  followupTypeName: string;
  leadName: string;
}

interface LeadLgoList {
  logId: number;
  leadId: number;
  statusId: number;
  statusName: string;
  followupConducted: number;
  followupTypeName: string;
  logNotes: string;
  assignedUser: number;
  assignedUserName: string;
  mediaTypeId: number;
  mediaTypeName: string;
  isNextActionRequired: boolean;
  taskId: number;
  incomingSource: string | null;
  incomingSourceName: string | null;
  incomingSubsourceId: number | null;
  incomingSubsourceName: string | null;
  eventTypeId: number;
  eventTypeName: string;
  interestedProject: string | null;
  interestedProjectName: string | null;
  taskNote: string | null;
  createdOn?: string;
}

interface UpdateStatusData {
  tenantId: number;
  leadId?: number;
  assignedUser?: number;
  isCompleted?: boolean;
  followupDate?: string;
  actionType?: string;
}

interface LeadDetailsViewProps {
  leadId: number;
  onAddTask: () => void;
  refreshTasks: boolean;
  onTasksRefreshed: () => void;
}

//hide task note event types
const TASK_NOTE_EVENT_TYPES = [2, 3, 4, 5, 7, 8, 9];

// Event Type Icon Mapping
const getEventTypeIcon = (eventTypeId: number): string => {
  const iconMap: { [key: number]: string } = {
    1: "bi-envelope", // New Inquire
    2: "bi-calendar-check", // Follow Up Scheduled
    3: "bi-check-circle", // Follow Up Conducted
    4: "bi-calendar-event", // Site Visit Scheduled
    5: "bi-geo-alt", // Site Visit Conducted
    6: "bi-arrow-repeat", // Reinquire
    7: "bi-people", // Builder Meet Scheduled
    8: "bi-x-octagon", // Builder Meet Canceled
    9: "bi-person-check", // Builder Meet Conducted
    10: "bi-clipboard-check", // Booking
    11: "bi-x-circle", // Booking Canceled
    12: "bi-check-circle-fill", // Finalized
    13: "bi-cart-check", // Purchased
    14: "bi-download", // External Lead Import
    15: "bi-info-circle", // Status Update
  };

  return iconMap[eventTypeId] || "bi-activity"; // Default icon
};

const getEventTypeColor = (eventTypeId: number): string => {
  const colorMap: { [key: number]: string } = {
    1: "#0d6efd", // New Inquire - blue
    2: "#198754", // Follow Up Scheduled - green
    3: "#28a745", // Follow Up Conducted - green
    4: "#0dcaf0", // Site Visit Scheduled - cyan
    5: "#17a2b8", // Site Visit Conducted - info
    6: "#fd7e14", // Reinquire - orange
    7: "#6610f2", // Builder Meet Scheduled - purple
    8: "#dc3545", // Builder Meet Canceled - red
    9: "#6f42c1", // Builder Meet Conducted - indigo
    10: "#20c997", // Booking - teal
    11: "#dc3545", // Booking Canceled - red
    12: "#ffc107", // Finalized - yellow
    13: "#198754", // Purchased - green
    14: "#6c757d", // External Lead Import - secondary
    15: "#0d6efd", // Status Update - blue
  };

  return colorMap[eventTypeId] || "#6c757d"; // Default gray
};

const UserTasksViewDashboard: React.FC<LeadDetailsViewProps> = ({
  leadId,
  onAddTask,
  refreshTasks,
  onTasksRefreshed,
}) => {
  const [activeTab, setActiveTab] = useState<string>("activity");
  const [followupType, setFollowupType] = useState<followupTypeList[]>([]);
  const [userTasks, setUserTasks] = useState<UserTaskInterface[]>([]);
  const [activityLogs, setActivityLogs] = useState<LeadLgoList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [updateStatusData, setUpdateStatusData] =
    useState<UpdateStatusData | null>(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchAllFollowUpType = await GetAllFollowUpTypes();
      const activeTypes = fetchAllFollowUpType.filter(
        (type: followupTypeList) =>
          type.isActive && type.id != 0 && type.name.toLowerCase() !== "note"
      );
      setFollowupType(activeTypes);

      const fetchedTasks = await GetUserTasksByLeadId(leadId);
      setUserTasks(fetchedTasks);

      const fetchedLogs = await GetLeadLog(leadId);
      const enrichedLogs = fetchedLogs.map((log) => {
        if (log.taskId) {
          const relatedTask = fetchedTasks.find(
            (task) => task.id === log.taskId
          );
          return {
            ...log,
            projectName:
              relatedTask?.projectName ||
              log.interestedProjectName ||
              "Unknown Project",
            leadName: relatedTask?.leadName || "Unknown Lead",
          };
        }
        return {
          ...log,
          projectName: log.interestedProjectName || "Unknown Project",
          leadName: "Unknown Lead",
        };
      });
      const sortedLogs = enrichedLogs.sort((a, b) => b.logId - a.logId);
      console.log("Sorted Logs:", sortedLogs);
      setActivityLogs(sortedLogs);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load tasks or logs. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [leadId]);

  useEffect(() => {
    if (refreshTasks) {
      fetchData().then(() => {
        onTasksRefreshed();
      });
    }
  }, [refreshTasks]);

  const redirectToUserTaskEdit = (taskId: number, actionType?: string) => {
    navigate("/UserTaskView", {
      state: { editTaskId: taskId, actionType },
    });
  };

  const handleUpdateStatus = async (taskId: number, actionType: string) => {
    try {
      const response = await GetUserTasksByLeadId(leadId);
      const matchedTask = response.find((task) => task.id === taskId);
      if (matchedTask) {
        const tenantId =
          localStorage.getItem("tenantId") || localStorage.getItem("tenant_id");
        if (!tenantId) {
          alert("Tenant ID not found in localStorage");
          return;
        }
        setUpdateStatusData({
          tenantId: parseInt(tenantId),
          leadId: matchedTask.leadId,
          assignedUser: matchedTask.assignedUser,
          isCompleted: matchedTask.isCompleted,
          followupDate: matchedTask.followupDate,
          actionType,
        });
        setIsUpdateStatusModalOpen(true);
      } else {
        throw new Error("No task data found");
      }
    } catch (error) {
      console.error("Error fetching task data for status update:", error);
      alert("Failed to fetch task data for status update");
    }
  };

  const getTaskStatus = (task: UserTaskInterface): string => {
    const now = new Date();
    const followupDate = new Date(task.followupDate);

    if (task.isCanceled) return "canceled";
    if (task.isCompleted) return "completed";
    // ✅ Booking type should never show overdue
    if (task.followupTypeName === "Booking") return "booked";
    if (task.isPostponed && task.rescheduleDate) return "postponed";
    if (followupDate < now && !task.isCompleted && !task.isCanceled)
      return "overdue";
    return "pending";
  };

  const getActivityLogTaskStatus = (log: LeadLgoList): string => {
    // ✅ Booking confirmed — always show "booked"
    if (log.eventTypeId === 10) return "booked";

    const relatedTask = userTasks.find((task) => task.id === log.taskId);
    if (relatedTask) {
      return getTaskStatus(relatedTask);
    }

    return "pending";
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+ [AP]M)/, "$1-$2-$3, $4");
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="mb-3">
            <i
              className="bi bi-exclamation-triangle"
              style={{ fontSize: "3rem", color: "#dc3545" }}
            ></i>
          </div>
          <h5 className="text-danger">{error}</h5>
        </div>
      </div>
    );
  }

  const followUpTasks = userTasks
    .filter((task) => task.followupTypeId !== 1)
    .sort((a, b) => b.id - a.id);

  const noteTasks = userTasks
    .filter((task) => task.followupTypeId === 1)
    .sort((a, b) => b.id - a.id);

  return (
    <div className="card">
      <ul
        className="nav nav-tabs cardHeaderHegiht"
        style={{ borderBottom: "1px solid rgb(222 226 230)" }}
        id="myTab"
        role="tablist"
      >
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHead ${activeTab === "activity" ? "active" : ""
              }`}
            id="tab-activity"
            onClick={() => setActiveTab("activity")}
            type="button"
            role="tab"
            aria-controls="tab-activity-pane"
            aria-selected={activeTab === "activity"}
          >
            <i className="bi bi-card-checklist"></i> Activity
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHead ${activeTab === "note" ? "active" : ""
              }`}
            id="tab-note"
            onClick={() => setActiveTab("note")}
            type="button"
            role="tab"
            aria-controls="tab-note-pane"
            aria-selected={activeTab === "note"}
          >
            <i className="bi bi-quote"></i> Note
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHead ${activeTab === "followup" ? "active" : ""
              }`}
            id="tab-followup"
            onClick={() => setActiveTab("followup")}
            type="button"
            role="tab"
            aria-controls="tab-followup-pane"
            aria-selected={activeTab === "followup"}
          >
            <i className="bi bi-stopwatch"></i> Follow-up
          </button>
        </li>
      </ul>

      <div className="tab-content-cardtask card-body p-0" id="myTabContent">
        <div
          className={`tab-pane fade ${activeTab === "activity" ? "show active" : ""
            }`}
          id="tab-activity-pane"
          role="tabpanel"
          aria-labelledby="tab-activity"
          tabIndex={0}
        >
          {activityLogs.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <i
                  className="bi bi-inbox"
                  style={{ fontSize: "3rem", color: "#6c757d" }}
                ></i>
              </div>
              <h5 className="text-muted">No records to show</h5>
              <p className="text-muted">No activity logs found.</p>
            </div>
          ) : (
            <div className="pb-3 activity-list position-relative">
              <div className="timeline-line"></div>
              {activityLogs.map((log) => {
                const logStatus = getActivityLogTaskStatus(log); // pending/completed/canceled/postponed/overdue
                const showStatusDropdown =
                  [2, 4, 7, 10].includes(log.eventTypeId) &&
                  log.followupTypeName !== "Note";
                const isBookingEvent = log.eventTypeId === 10;

                const statusBadgeClass =
                  logStatus === "completed"
                    ? "success"
                    : logStatus === "canceled"
                      ? "danger"
                      : logStatus === "postponed"
                        ? "info"
                        : logStatus === "overdue"
                          ? "warning"
                          : "secondary";

                // 🚫 Do NOT show badge for these event types or for Note
                const hideBadgeEventTypes = [1, 6, 12, 13, 14, 15];
                const showStatusBadge =
                  !hideBadgeEventTypes.includes(log.eventTypeId) &&
                  log.followupTypeName !== "Note" &&
                  logStatus !== "completed";

                return (
                  <div
                    key={log.logId}
                    className="d-flex position-relative activity-item"
                  >
                    <div className="timeline-circle"></div>
                    <div className="flex-grow-1 ms-4">
                      <div className="card border logData-card">
                        <div className="card-body p-3 position-relative">
                          <div className="d-flex align-items-start">
                            <div className="me-3 mt-1">
                              <i
                                className={`bi ${getEventTypeIcon(
                                  log.eventTypeId
                                )}`}
                                style={{
                                  fontSize: "1.1rem",
                                  color: getEventTypeColor(log.eventTypeId),
                                }}
                              ></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="mb-2">
                                <span className="log-notes">
                                  {log.followupConducted === 1
                                    ? log.taskNote || "No notes"
                                    : log.logNotes || "No notes"}
                                </span>
                              </div>
                              <div className="text-muted log-details">
                                <span>
                                  {formatDateTime(log.createdOn ?? "")}
                                </span>
                                <span className="mx-2">|</span>
                                <span>{log.assignedUserName}</span>
                              </div>
                              <div className="text-muted mt-2 task-details">
                                {/* Show Task Note only for specific event types AND when a note exists */}
                                {TASK_NOTE_EVENT_TYPES.includes(
                                  log.eventTypeId
                                ) &&
                                  log.taskNote && (
                                    <span>Note: {log.taskNote}</span>
                                  )}

                                {showStatusBadge && (
                                  <span
                                    className={`float-end ${!TASK_NOTE_EVENT_TYPES.includes(
                                      log.eventTypeId
                                    ) || !log.taskNote
                                      ? ""
                                      : ""
                                      } ${!showStatusDropdown ? "mx-4" : ""}`}
                                  >
                                    <span
                                      className={`badge bg-${statusBadgeClass}`}
                                      style={{ textTransform: "capitalize" }}
                                    >
                                      {logStatus}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Status dropdown – now for event types 2, 4, 7, 10 */}
                            {showStatusDropdown && (
                              <div className="dropdown">
                                <button
                                  className="btn btn-sm p-1 dropdown-toggle"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  disabled={[
                                    "completed",
                                    "canceled",
                                    "postponed",
                                  ].includes(logStatus)}
                                >
                                  <i className="bi bi-three-dots-vertical"></i>
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                  {/* Always allow Cancel */}
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() =>
                                        handleUpdateStatus(
                                          log.taskId,
                                          "canceled"
                                        )
                                      }
                                    >
                                      <i className="bi bi-x-circle me-2 text-danger"></i>{" "}
                                      Cancel
                                    </button>
                                  </li>

                                  {/* For non-booking events allow Complete & Postpone */}
                                  {!isBookingEvent && (
                                    <>
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleUpdateStatus(
                                              log.taskId,
                                              "completed"
                                            )
                                          }
                                        >
                                          <i className="bi bi-check-circle me-2 text-success"></i>{" "}
                                          Complete
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleUpdateStatus(
                                              log.taskId,
                                              "postponed"
                                            )
                                          }
                                        >
                                          <i className="bi bi-calendar-check me-2 text-info"></i>{" "}
                                          Postpone
                                        </button>
                                      </li>
                                    </>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div
          className={`tab-pane fade ${activeTab === "followup" ? "show active" : ""
            }`}
          id="tab-followup-pane"
          role="tabpanel"
          aria-labelledby="tab-followup"
          tabIndex={0}
        >
          {followUpTasks.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <i
                  className="bi bi-inbox"
                  style={{ fontSize: "3rem", color: "#6c757d" }}
                ></i>
              </div>
              <h5 className="text-muted">No records to show</h5>
              <p className="text-muted">No follow-up tasks found.</p>
            </div>
          ) : (
            <div className="cardtask pb-3 position-relative">
              <div className="timeline-line"></div>
              {followUpTasks.map((task) => {
                const taskStatus = getTaskStatus(task);
                const isBookingFollowup = task.followupTypeName === "Booking";

                // In activityLogs.map() and followUpTasks.map() — update statusBadgeClass
                const statusBadgeClass =
                  taskStatus === "completed" ? "success" :
                    taskStatus === "canceled" ? "danger" :
                      taskStatus === "postponed" ? "info" :
                        taskStatus === "overdue" ? "warning" :
                          taskStatus === "booked" ? "primary" :  // ✅ add this
                            "secondary";

                // Message text
                const message = isBookingFollowup
                  ? task.projectName && task.projectName.trim() !== ""
                    ? `The booking for ${task.projectName
                    } has been confirmed on ${formatDateTime(
                      task.followupDate
                    )} by the Admin.`
                    : `The booking has been confirmed on ${formatDateTime(
                      task.followupDate
                    )} by the Admin.`
                  : task.projectName && task.projectName.trim() !== ""
                    ? `The ${task.followupTypeName} is scheduled for ${task.projectName
                    } on ${formatDateTime(task.followupDate)} by ${task.assignedUserName || `User ${task.createdBy}`
                    }.`
                    : `The ${task.followupTypeName
                    } is scheduled on ${formatDateTime(
                      task.followupDate
                    )} by ${task.assignedUserName || `User ${task.createdBy}`
                    }.`;

                return (
                  <div
                    key={task.id}
                    className="d-flex position-relative task-item"
                  >
                    <div className="timeline-circle"></div>
                    <div className="flex-grow-1 ms-4">
                      <div className="card border logData-card">
                        <div className="card-body hidecard-overflow-xaxics p-3 position-relative">
                          <div className="d-flex align-items-start">
                            <div className="flex-grow-1">
                              <div className="mb-2">
                                <span className="task-notes">{message}</span>
                              </div>
                              <div className="text-muted task-details">
                                <span>
                                  Remarks: {task.taskNotes || "No remarks"}
                                </span>
                                <span className="float-end">
                                  <span
                                    className={`badge bg-${statusBadgeClass} me-2`}
                                    style={{ textTransform: "capitalize" }}
                                  >
                                    {taskStatus}
                                  </span>
                                </span>
                              </div>
                            </div>
                            <div className="dropdown">
                              <button
                                className="btn btn-sm p-1 dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                disabled={[
                                  "completed",
                                  "canceled",
                                  "postponed",
                                ].includes(taskStatus)}
                              >
                                <i className="bi bi-three-dots-vertical"></i>
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end">
                                {/* Always show Cancel for all follow-ups */}
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() =>
                                      handleUpdateStatus(task.id, "canceled")
                                    }
                                  >
                                    <i className="bi bi-x-circle me-2 text-danger"></i>{" "}
                                    Cancel Task
                                  </button>
                                </li>

                                {/* For non-booking follow-ups, also allow Complete & Postpone */}
                                {!isBookingFollowup && (
                                  <>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() =>
                                          handleUpdateStatus(
                                            task.id,
                                            "completed"
                                          )
                                        }
                                      >
                                        <i className="bi bi-check-circle me-2 text-success"></i>{" "}
                                        Complete Task
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() =>
                                          handleUpdateStatus(
                                            task.id,
                                            "postponed"
                                          )
                                        }
                                      >
                                        <i className="bi bi-calendar-check me-2 text-info"></i>{" "}
                                        Postpone Task
                                      </button>
                                    </li>
                                  </>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div
          className={`tab-pane fade ${activeTab === "note" ? "show active" : ""
            }`}
          id="tab-note-pane"
          role="tabpanel"
          aria-labelledby="tab-note"
          tabIndex={0}
        >
          {noteTasks.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <i
                  className="bi bi-inbox"
                  style={{ fontSize: "3rem", color: "#6c757d" }}
                ></i>
              </div>
              <h5 className="text-muted">No records to show</h5>
              <p className="text-muted">No notes found.</p>
            </div>
          ) : (
            <div className="cardtask pb-3 position-relative">
              <div className="timeline-line"></div>
              {noteTasks.map((task) => (
                <div
                  key={task.id}
                  className="d-flex position-relative task-item"
                >
                  <div className="timeline-circle"></div>
                  <div className="flex-grow-1 ms-4">
                    <div className="card border logData-card">
                      <div className="card-body p-3 hidecard-overflow-xaxics position-relative">
                        <div className="d-flex align-items-start">
                          <div className="flex-grow-1">
                            <div className="mb-2">
                              <span className="task-notes">
                                {task.taskNotes || "No note"}
                              </span>
                            </div>

                            <div className="text-muted small">
                              {formatDateTime(task.followupDate)} |{" "}
                              {task.assignedUserName ||
                                `User ${task.createdBy}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {isUpdateStatusModalOpen && updateStatusData && (
        <UpdatedUserTaskComponent
          tenantId={updateStatusData.tenantId}
          leadId={updateStatusData.leadId}
          assignedUser={updateStatusData.assignedUser}
          isCompleted={updateStatusData.isCompleted}
          followupDate={updateStatusData.followupDate}
          actionType={updateStatusData.actionType}
          isOpen={isUpdateStatusModalOpen}
          onClose={() => {
            setIsUpdateStatusModalOpen(false);
            setUpdateStatusData(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default UserTasksViewDashboard;
